import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { Tabs, Column, Row, Skeleton, Tag, IconButton } from 'components/Blocks';
import { GoBackButton } from 'components/Buttons';
import { ChevronLeftIcon, ChevronRightIcon } from 'components/Icons';

import { RepriceRequest } from './RepriceRequest';
import { TaskDateChanges } from './TaskDateChanges';
import { ProjectDateChange } from './ProjectDateChange';
import { TaskAssign } from './TaskAssign';
import { ProjectAssign } from './ProjectAssign';
import { ProjectStartAuthorization } from './ProjectStartAuthorization';
import { OutsourceAuthorization } from './OutsourceAuthorization';
import { InvoiceAssign } from './InvoiceAssign';
import { QuotationRequests } from './QuotationRequest';
import { AddCountryRequest } from './AddCountryRequest';
import { DiscountRequest } from './DiscountRequest';
import { InvoiceControlCancelation } from './InvoiceControlCancel';
import { InvoiceCancelation } from './InvoiceCancelation';
import { RecruitmentAssign } from './RecruitmentAssign';
import { ProjectTransfers } from './ProjectTransfers';

import { NoActions } from './NoActions';
import { ProjectLoadWrapper } from './LocalActionWrapper';

import { FullPendingActions, usePendingActions } from 'providers/PendingActions';

import { useUrlState } from 'hooks/urlState';

import { findIndexOrNull, getFirst, getLastIndex, listGet } from 'utils/array';

const components = {
  UNASSIGNED_TASKS: TaskAssign,

  UNASSIGNED_PROJECTS: ProjectAssign,

  UNASSIGNED_INVOICE_CONTROLS: InvoiceAssign,

  PROJECT_START_AUTHORIZATION: ProjectStartAuthorization,

  PROJECT_REPRICE_REQUESTS: RepriceRequest,

  TASK_DATE_CHANGE: TaskDateChanges,
  PROJECT_DATE_CHANGE: ProjectDateChange,

  OUTSOURCE_AUTHORIZATION: OutsourceAuthorization,

  ADD_COUNTRY_TO_PROJECT: AddCountryRequest,

  PENDING_QUOTATION_REQUESTS: QuotationRequests,

  ACTIVE_DISCOUNT_REQUESTS: DiscountRequest,

  INVOICE_CANCELATION_REQUESTS: InvoiceCancelation,

  UNASSIGNED_RECRUITMENTS: RecruitmentAssign,

  PROJECT_TEAM_CHANGE_REQUESTS: ProjectTransfers,

  INVOICE_CONTROL_CANCEL_REQUESTS: InvoiceControlCancelation,
} as Record<FullPendingActions, () => JSX.Element>;

function PendingActions() {
  const { urlParams, updateUrlParams } = useUrlState<{ tab: string }>();

  const { t } = useLang();

  const { pendingEntries, loading } = usePendingActions();

  const tabs = useMemo(
    () =>
      Object.values(pendingEntries)
        .filter(({ count }) => count)
        .map(({ getLabel, ...config }) => ({
          ...config,
          value: config.queryParam,
          label: (
            <>
              <span>{getLabel(t)}</span>
              <Tag name={config.count} color="primary" />
            </>
          ),
        })),
    [pendingEntries, t],
  );

  const tab = listGet(tabs, urlParams.tab, 'queryParam') ?? getFirst(tabs);

  if (loading) return <Skeleton height={420} />;

  if (!tab) return <NoActions />;

  const TabComponent = components[tab.action];

  const nextTab = () => {
    const currentIndex = tabs.findIndex((t) => t.value === tab.value);

    const nextTab = tabs[currentIndex + 1] ?? getFirst(tabs);

    updateUrlParams({ tab: nextTab.value });
  };
  const prevTab = () => {
    const currentIndex = findIndexOrNull(tabs, tab.value, 'value');

    // If zero or null we cycle back to the last
    const prevIndex = currentIndex ? currentIndex - 1 : getLastIndex(tabs);

    updateUrlParams({ tab: tabs[prevIndex].value });
  };

  return (
    <Column className="phone:gap-6">
      <Column gap="2">
        <Row>
          <GoBackButton />

          <h5 className="text-3xl font-semibold capitalize text-primary">{t('pending-actions')}</h5>
        </Row>

        <Row className="phone:mt-2 phone:justify-between">
          {tabs.length > 1 && (
            <IconButton className="hidden phone:flex" onClick={prevTab}>
              <ChevronLeftIcon size={14} />
            </IconButton>
          )}

          <Tabs
            value={tab.queryParam}
            tabs={tabs}
            onTabChange={(tab) => updateUrlParams({ tab })}
            type="highlighted"
            responsiveBreakpoint="SMALL_TABLET"
            tabListClassName="small_tablet:items-start"
            tabClassName="small_tablet:justify-between small_tablet:w-full"
          />

          {tabs.length > 1 && (
            <IconButton className="hidden phone:flex" onClick={nextTab}>
              <ChevronRightIcon size={14} />
            </IconButton>
          )}
        </Row>
      </Column>

      <ProjectLoadWrapper currentAction={tab.action}>
        <TabComponent />
      </ProjectLoadWrapper>
    </Column>
  );
}

export default PendingActions;
